/**
 * Tiny fetch wrapper used by every TanStack Query hook in the console.
 *
 * - Attaches the Entra bearer token from MSAL (the Shell wires the token
 *   acquisition; this module only consumes a getter).
 * - Surfaces the OpenAPI error envelope: `{ error: { code, message, field?, details? } }`.
 * - Handles ETag round-tripping so optimistic-locking on persona writes
 *   "just works" — callers stash the returned etag and pass it back via
 *   `If-Match` on the next mutate.
 */
export interface ApiError {
  code: string
  message: string
  field?: string
  details?: unknown
}

export class ApiException extends Error {
  constructor(public readonly status: number, public readonly error: ApiError) {
    super(`${error.code}: ${error.message}`)
  }
}

export interface ApiResponse<T> {
  data: T
  etag?: string
}

let tokenGetter: () => Promise<string | null> = async () => null

export function configureTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = new Headers(init.headers)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const token = await tokenGetter()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(path, { ...init, headers })
  const etag = res.headers.get('ETag') ?? undefined
  const ct = res.headers.get('Content-Type') ?? ''
  const isJson = ct.includes('application/json')

  if (!res.ok) {
    const payload = isJson ? await res.json().catch(() => ({})) : {}
    const error: ApiError = payload?.error ?? {
      code: `HTTP_${res.status}`,
      message: res.statusText || 'Request failed',
    }
    throw new ApiException(res.status, error)
  }

  if (res.status === 204) {
    return { data: undefined as unknown as T, etag }
  }
  const data = isJson ? await res.json() : (await res.text() as unknown as T)
  return { data, etag }
}
